import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Star, UserPlus, AlertTriangle, CheckCircle2, LogIn } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Button, Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface InviteInfoResponse {
  familyName: string;
  invitedByName: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
}

interface AcceptResponse {
  message: string;
}

// ---------------------------------------------------------------------------
// Error type discriminator
// ---------------------------------------------------------------------------
type InviteError = 'expired' | 'accepted' | 'revoked' | 'not_found' | 'unknown';

function getInviteError(status: string): InviteError {
  if (status === 'accepted') return 'accepted';
  if (status === 'revoked') return 'revoked';
  if (status === 'expired') return 'expired';
  return 'unknown';
}

const errorMessages: Record<InviteError, { title: string; body: string }> = {
  expired: {
    title: 'This invitation has expired',
    body: 'Ask the family admin to generate a new invite link.',
  },
  accepted: {
    title: 'This invitation has already been used',
    body: 'Someone has already accepted this invitation. Sign in or contact the family admin.',
  },
  revoked: {
    title: 'This invitation was revoked',
    body: 'This invite link is no longer valid. Ask the family admin for a new one.',
  },
  not_found: {
    title: 'Invitation not found',
    body: 'This link may be invalid. Please check the link and try again.',
  },
  unknown: {
    title: 'Something went wrong',
    body: "We couldn't load this invitation. Please try again.",
  },
};

// ---------------------------------------------------------------------------
// InviteAcceptPage
// ---------------------------------------------------------------------------
export default function InviteAcceptPage(): React.ReactElement {
  const { familyId, token } = useParams<{ familyId: string; token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser } = useAuth();

  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  // Fetch invite info — public endpoint, no auth required
  const { data: inviteData, isLoading, error: fetchError } = useQuery<InviteInfoResponse>({
    queryKey: ['invite', familyId, token],
    queryFn: () => get<InviteInfoResponse>(`/invite/${familyId ?? ''}/${token ?? ''}`),
    enabled: Boolean(familyId) && Boolean(token),
    retry: false,
  });

  // Accept mutation
  const acceptMutation = useMutation({
    mutationFn: () => post<AcceptResponse>(`/invite/${familyId ?? ''}/${token ?? ''}/accept`),
    onSuccess: async () => {
      setAcceptSuccess(true);
      await refreshUser(); // pull updated familyId/memberId into auth context
      setTimeout(() => navigate('/dashboard'), 2500);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to accept invitation.';
      setAcceptError(message);
    },
  });

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-sm text-slate-500">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // ---- Not found / fetch error ----
  if (fetchError || !inviteData) {
    const errInfo = errorMessages['not_found'];
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
        <Card className="w-full max-w-sm text-center" shadow="md">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-100">
              <AlertTriangle size={28} className="text-danger-600" aria-hidden="true" />
            </div>
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">{errInfo.title}</h1>
          <p className="text-sm text-slate-500 mb-6">{errInfo.body}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            <LogIn size={15} aria-hidden="true" />
            Go to sign in
          </Link>
        </Card>
      </div>
    );
  }

  // ---- Invalid status ----
  if (inviteData.status !== 'pending') {
    const errInfo = errorMessages[getInviteError(inviteData.status)];
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
        <Card className="w-full max-w-sm text-center" shadow="md">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-100">
              <AlertTriangle size={28} className="text-danger-600" aria-hidden="true" />
            </div>
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">{errInfo.title}</h1>
          <p className="text-sm text-slate-500 mb-6">{errInfo.body}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            <LogIn size={15} aria-hidden="true" />
            Go to sign in
          </Link>
        </Card>
      </div>
    );
  }

  // ---- Success ----
  if (acceptSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
        <Card className="w-full max-w-sm text-center" shadow="md">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100">
              <CheckCircle2 size={28} className="text-secondary-600" aria-hidden="true" />
            </div>
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">You've joined {inviteData.familyName}!</h1>
          <p className="text-sm text-slate-500">Redirecting you to the dashboard...</p>
        </Card>
      </div>
    );
  }

  // ---- Main: pending invite ----
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
      {/* Logo */}
      <div className="mb-8 flex items-center gap-2 text-primary-700">
        <Star size={28} className="text-accent-500 fill-accent-400" aria-hidden="true" />
        <span className="text-2xl font-extrabold tracking-tight">KidKred</span>
      </div>

      <Card className="w-full max-w-sm text-center" shadow="md">
        {/* Invite illustration */}
        <div className="mb-5 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-5xl">
            👨‍👩‍👧‍👦
          </div>
        </div>

        <h1 className="text-xl font-bold text-slate-900 mb-1">
          You've been invited!
        </h1>
        <p className="text-sm text-slate-600 mb-2">
          <span className="font-semibold">{inviteData.invitedByName}</span> has invited you to join the{' '}
          <span className="font-semibold text-primary-700">{inviteData.familyName}</span> family on KidKred.
        </p>

        <div className="my-6">
          {isAuthenticated ? (
            // Logged in: show Accept button
            <div className="flex flex-col gap-3">
              {acceptError && (
                <p role="alert" className="text-xs text-danger-600">{acceptError}</p>
              )}
              <Button
                fullWidth
                size="lg"
                isLoading={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate()}
                leftIcon={<UserPlus size={18} />}
              >
                Accept Invitation
              </Button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className={cn(
                  'text-sm text-slate-500 hover:text-slate-700',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded',
                )}
              >
                Decline
              </button>
            </div>
          ) : (
            // Not logged in: prompt to sign in first
            <div className="flex flex-col gap-3">
              <p className="text-sm text-slate-500 mb-1">
                Sign in to accept this invitation.
              </p>
              <Link to={`/login?redirect=/invite/${familyId ?? ''}/${token ?? ''}`}>
                <Button
                  fullWidth
                  size="lg"
                  leftIcon={<LogIn size={18} />}
                >
                  Sign in to Accept
                </Button>
              </Link>
              <p className="text-xs text-slate-400">
                Don't have an account? Ask your family admin to add you directly.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
